import { Outlet, useNavigate, useSearchParams } from 'react-router';
import { useEffect, useState } from 'react';

import { userHasPermission } from '@utils/access';
import type { User } from '@models/user';
import { getUserTokenFromLocalStorage, setUserTokenToLocalStorage } from '@utils/localStorage';

import { useMeLoginStore, useMeStore } from '@features/users/store';
import { useMeQuery } from '@features/users/queries';
import { useToast } from '@hooks/useToast';

import LoadingAnimation from '@components/LoadingAnimation';

type PrivateRouteProps = {
  permission?: string;
};

const NavigationPaths: { [path: string]: string } = {
  login: '/',
  dashboard: '/dashboard',
};

const BackendPassedTokenKey = 'accessToken';

function useAuthTokenFromQuery() {
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const accessToken = searchParams.get(BackendPassedTokenKey);
    if (accessToken) {
      setUserTokenToLocalStorage(accessToken);
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
}

function PrivateRoute({ permission }: PrivateRouteProps) {
  const [isInitial, setIsInitial] = useState(true);
  const toast = useToast();

  useEffect(() => {
    setIsInitial(false);
  }, []);
  const navigate = useNavigate();
  const meUser = useMeStore();
  const login = useMeLoginStore();
  useAuthTokenFromQuery();

  const token = getUserTokenFromLocalStorage();
  const shouldRefetch = !!token && !meUser; // Only fetch if user is not in store
  const { data: me, isLoading } = useMeQuery(shouldRefetch);

  useEffect(() => {
    if (me && !meUser) login(me as User);
  }, [me, meUser, login]);

  useEffect(() => {
    const navigateTo = (path: string) => {
      navigate(path, { replace: true });
      return null;
    };

    // Prevent redirect loop while loading ain't yet started and token is absent
    if (isInitial) return;

    if (!token) {
      navigateTo(NavigationPaths.login);
      return;
    }

    if (meUser && permission && !userHasPermission(meUser, permission)) {
      toast.error(`You don't have - "${permission}" - permission to access this page`);
      navigateTo(NavigationPaths.dashboard);
      return;
    }
    if (!isLoading && !me) {
      navigateTo(NavigationPaths.login);
      return;
    }
  }, [isInitial, token, meUser, toast, permission, isLoading, me, navigate]);

  if (isLoading) return <LoadingAnimation />;

  return <Outlet />;
}

export default PrivateRoute;
